import type { IClassDropdownItem } from '@/types';
import type { IAddStudentPayload } from '@/types/payload';

import { enqueueSnackbar } from 'notistack';
import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';

import CustomModal from '@/components/CustomModal';
import Input from '@/components/Input/Input';
import Select from '@/components/Select';
import { GENDER_OPTIONS } from '@/constants/constant';
import { useFormatText } from '@/hooks/useFormatText';
import {
  addStudentFormSchema,
  type TAddStudentForm,
} from '@/schema/addStudentSchema';
import { getClassDropdown } from '@/service/classService';
import { addStudent } from '@/service/studentService';
import { zodResolver } from '@hookform/resolvers/zod';

interface IAddStudentModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  defaultClassId?: string;
}

export const AddStudentModal = ({
  isOpen,
  onClose,
  onSuccess,
  defaultClassId,
}: IAddStudentModalProps) => {
  const { formatText } = useFormatText();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [classOptions, setClassOptions] = useState<
    { value: string; label: string }[]
  >([]);

  const {
    register,
    handleSubmit,
    watch,
    setValue,
    reset,
    formState: { errors },
  } = useForm<TAddStudentForm>({
    resolver: zodResolver(addStudentFormSchema),
    defaultValues: {
      name: '',
      studentId: '',
      gender: '',
      email: '',
      classId: defaultClassId || '',
    },
  });

  const selectedClass = watch('classId');
  const selectedGender = watch('gender');

  useEffect(() => {
    const fetchClassDropdown = async () => {
      try {
        const res = await getClassDropdown(true);
        if (res?.data) {
          const options = res.data.map((item: IClassDropdownItem) => ({
            value: item.id,
            label: item.name,
          }));
          setClassOptions(options);
        }
      } finally {
        // Handle loading state if needed
      }
    };

    if (isOpen) {
      fetchClassDropdown();
    }
  }, [isOpen]);

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleFormSubmit = async (data: TAddStudentForm) => {
    setIsSubmitting(true);

    try {
      const payload: IAddStudentPayload = {
        name: data.name,
        student_id: data.studentId,
        gender: data.gender,
        email: data.email,
        class_id: data.classId,
        send_email: true,
      };

      const response = await addStudent(payload);

      if (response?.status) {
        enqueueSnackbar(response?.message, { variant: 'success' });
        handleClose();
        onSuccess?.();
      } else {
        enqueueSnackbar(response?.message, { variant: 'error' });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <CustomModal
      setIsOpen={onClose}
      onClose={handleClose}
      position="right"
      isHeader
      heading={formatText('classDetails.addStudentModal.title')}
      isFooter
      cancelButtonProps={{
        label: formatText('classDetails.addStudentModal.actions.cancel'),
        onClick: handleClose,
        variant: 'link',
      }}
      confirmButtonProps={{
        label: formatText('classDetails.addStudentModal.actions.addStudent'),
        onClick: handleSubmit(handleFormSubmit),
        loading: isSubmitting,
        disabled: isSubmitting,
      }}
    >
      <form className="w-full space-y-4" data-testid="add-student-form">
        {/* Student Name */}
        <Input
          name="name"
          label={formatText('classDetails.addStudentModal.form.studentName')}
          placeholder={formatText(
            'classDetails.addStudentModal.form.studentNamePlaceholder'
          )}
          register={register}
          error={
            errors.name?.message ? formatText(errors.name.message) : undefined
          }
          required
          dataTestId="student-name-input"
        />

        {/* Student ID */}
        <Input
          name="studentId"
          label={formatText('classDetails.addStudentModal.form.studentId')}
          placeholder={formatText(
            'classDetails.addStudentModal.form.studentIdPlaceholder'
          )}
          register={register}
          error={
            errors.studentId?.message
              ? formatText(errors.studentId.message)
              : undefined
          }
          required
          dataTestId="student-id-input"
        />

        {/* Gender */}
        <Select
          // name="gender"
          name={formatText('classDetails.addStudentModal.form.gender')}
          options={GENDER_OPTIONS.map((o) => ({
            value: o.value,
            label: formatText(o.label),
          }))}
          selected={selectedGender}
          onChange={(value: string | string[]) =>
            setValue('gender', value as string, { shouldValidate: true })
          }
          placeholder={formatText(
            'classDetails.addStudentModal.form.genderPlaceholder'
          )}
          error={
            errors.gender?.message
              ? formatText(errors.gender.message)
              : undefined
          }
          isRequired
          dataTestId="student-gender-select"
        />

        {/* Email */}
        <Input
          name="email"
          type="email"
          label={formatText('classDetails.addStudentModal.form.email')}
          placeholder={formatText(
            'classDetails.addStudentModal.form.emailPlaceholder'
          )}
          register={register}
          error={
            errors.email?.message ? formatText(errors.email.message) : undefined
          }
          required
          dataTestId="student-email-input"
        />

        {/* Class */}
        <Select
          name={formatText('classDetails.addStudentModal.form.class')}
          options={classOptions}
          selected={selectedClass}
          onChange={(value: string | string[]) =>
            setValue('classId', value as string, {
              shouldValidate: true,
            })
          }
          placeholder={formatText(
            'classDetails.addStudentModal.form.classPlaceholder'
          )}
          error={
            errors.classId?.message
              ? formatText(errors.classId.message)
              : undefined
          }
          disabled={!!defaultClassId}
          isRequired
          dataTestId="student-class-select"
        />
      </form>
    </CustomModal>
  );
};
